// Settings → Profile & security (R10, screen 25): your own account. Everyone
// has it, whatever their rung. `/admin/settings/security` redirects here.
//
//   You       name, email, role — read-only: nothing in the product edits
//             them yet, and the design's Edit links would need a route the
//             brief does not name. An Owner or Admin changes people on Users.
//   Security  password (the one Change password modal), two-factor (the whole
//             T2 flow, SecuritySettings), and sessions — "Sign out others"
//             ends every other session of this login (playbook §4).
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MeResponse, adminMe, mySessionsApi, revokeOtherSessionsApi } from '../../api/admin';
import { ChangePasswordModal } from '../../components/PasswordDialogs';
import { RowCard } from '../../components/fs';
import { useToast } from '../../components/Toast';
import SecuritySettings from './SecuritySettings';

const ROLE_WORD: Record<string, string> = { owner: 'Owner', admin: 'Admin', member: 'Member' };

/** "today", "3 days ago", "2 months ago" — how long ago a password was set. */
export function agoWords(iso: string, now = Date.now()): string {
  const days = Math.floor((now - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 14) return `${days} days ago`;
  if (days < 60) return `${Math.round(days / 7)} weeks ago`;
  if (days < 365) return `${Math.round(days / 30)} months ago`;
  const years = Math.round(days / 365);
  return `${years} year${years === 1 ? '' : 's'} ago`;
}

export default function ProfileSettings() {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [changing, setChanging] = useState(false);
  const toast = useToast();

  const loadMe = useCallback(() => { adminMe().then(setMe).catch(() => undefined); }, []);
  useEffect(loadMe, [loadMe]);

  const admin = me?.admin;
  const roleWord = admin ? (ROLE_WORD[admin.role] || admin.role) : '';
  const runsWorkspace = !!me && (me.isPlatformAdmin || admin?.role === 'owner' || admin?.role === 'admin');

  return (
    <div className="st-col">
      <div className="fs-page-head">
        <div className="text">
          <h1>Profile &amp; security</h1>
          <div className="fs-page-sub">Your own account</div>
        </div>
      </div>

      <section className="fs-card st-card" aria-labelledby="st-you">
        <div className="st-card-head"><h2 id="st-you">You</h2></div>
        <RowCard label="Name">{admin ? (admin.name || <span className="st-muted">Not set</span>) : '…'}</RowCard>
        <RowCard label="Email">{admin?.email || '…'}</RowCard>
        <RowCard
          label="Role"
          hint={runsWorkspace ? undefined : 'An Owner or Admin changes names, emails and roles.'}
          actions={runsWorkspace ? <Link className="fs-btn text" to="/admin/settings/users">Manage users</Link> : undefined}
        >
          {admin ? <>{roleWord}{me?.account ? <> of {me.account.name}</> : null}</> : '…'}
        </RowCard>
      </section>

      <section className="fs-card st-card" aria-labelledby="st-security">
        <div className="st-card-head"><h2 id="st-security">Security</h2></div>
        <RowCard
          label="Password"
          actions={<button type="button" className="fs-btn text" onClick={() => setChanging(true)}>Change password</button>}
        >
          {admin?.password_changed_at ? `Changed ${agoWords(admin.password_changed_at)}` : <span className="st-muted">Never changed here</span>}
        </RowCard>
        <SecuritySettings />
        <SessionsRow />
      </section>

      {changing && admin && (
        <ChangePasswordModal email={admin.email} onClose={() => setChanging(false)}
          onDone={() => { setChanging(false); toast('Your password has been changed.'); loadMe(); }} />
      )}
    </div>
  );
}

/**
 * The Sessions row. The store keeps no device names, so it says how many
 * other sessions there are rather than inventing a list. Signing the others
 * out asks once more first; this tab stays signed in.
 */
function SessionsRow() {
  const [others, setOthers] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const toast = useToast();

  const load = useCallback(() => {
    mySessionsApi().then((r) => setOthers(r.others)).catch(() => setOthers(null));
  }, []);
  useEffect(load, [load]);

  async function signOutOthers() {
    setBusy(true); setError('');
    try {
      const r = await revokeOtherSessionsApi();
      setConfirming(false);
      toast(r.ended === 1 ? 'Signed out of 1 other session.' : `Signed out of ${r.ended} other sessions.`);
      load();
    } catch (e: any) {
      setError(e.message || 'Could not sign the other sessions out.');
    } finally { setBusy(false); }
  }

  const words = others == null ? 'This device (now)'
    : others === 0 ? 'This device (now) · no other sessions'
      : `This device (now) · ${others} other session${others === 1 ? '' : 's'}`;

  return (
    <RowCard
      label="Sessions"
      hint="Everywhere this login is signed in."
      actions={confirming ? (
        <>
          <button type="button" className="fs-btn danger sm" disabled={busy} onClick={() => void signOutOthers()}>
            {busy ? 'Signing out…' : 'Sign them out'}
          </button>
          <button type="button" className="fs-btn sm" disabled={busy} onClick={() => setConfirming(false)}>Cancel</button>
        </>
      ) : (
        <button type="button" className="fs-btn text danger" onClick={() => { setConfirming(true); setError(''); }}>
          Sign out others
        </button>
      )}
    >
      {words}
      {confirming && (
        <div className="st-muted st-note">
          Every other browser and device signed in as you goes back to the sign-in page. This one stays.
        </div>
      )}
      {error && <div className="ad-err" style={{ marginTop: 8 }}>{error}</div>}
    </RowCard>
  );
}
